import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { semanticColor } from "@how/design-system";
import { ConfirmActionModal } from "@how/ui";
import { isPrivilegedRole } from "./role-catalog";
import { RoleChoices } from "./RoleChoices";
import {
  describeStatusError,
  describeTeamError,
  useSetMemberCampuses,
  useSetMemberRoles,
  useSetMembershipStatus,
  type CustomRole,
  type LoginStatusChange,
  type TeamMember,
} from "./team-queries";

export interface MemberAccessModalProps {
  /** The login being changed; null when closed. */
  readonly member: TeamMember | null;
  readonly customRoles: readonly CustomRole[];
  /** The church's campuses; shown only when the church has multi-campus. */
  readonly campuses: readonly {
    readonly id: string;
    readonly name: string;
    readonly isActive: boolean;
  }[];
  readonly multiCampus: boolean;
  /** Whether the reader is an owner: only an owner gives or takes away the owner role. */
  readonly readerIsOwner: boolean;
  readonly canManage: boolean;
  /** The reader's own login: nobody suspends or removes themselves here. */
  readonly readerUserId: string | null;
  /** The member is the church's only active owner: they cannot be suspended or removed. */
  readonly isLastOwner: boolean;
  readonly onClose: () => void;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

/** An API key's login (ADR 0053): a membership with a made-up address, shown as a key, not a person. */
export function isApiKeyLogin(member: TeamMember): boolean {
  return member.email !== null && /^api\+.*\.how\.invalid$/.test(member.email);
}

export function memberName(member: TeamMember): string {
  if (isApiKeyLogin(member)) return "API key";
  return member.displayName.trim().length > 0
    ? member.displayName
    : (member.email ?? "Someone without a name yet");
}

/**
 * One person's access: their roles (built-in and the church's own), and, in a multi-campus church, the campuses they
 * work with. users.manage changes both; the owner rules (only an owner grants owner, the last owner stays) and the
 * campus rule (owners and administrators see every campus) are the database's, said here before anyone trips them.
 */
export function MemberAccessModal({
  member,
  customRoles,
  campuses,
  multiCampus,
  readerIsOwner,
  canManage,
  readerUserId,
  isLastOwner,
  onClose,
}: MemberAccessModalProps) {
  const setRoles = useSetMemberRoles();
  const setCampuses = useSetMemberCampuses();
  // The page remounts this dialog per member (its key), so the form starts from the member being opened.
  const [roleKeys, setRoleKeys] = useState<string[]>([...(member?.roleKeys ?? [])]);
  const [campusIds, setCampusIds] = useState<string[]>([...(member?.campusIds ?? [])]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const holdsOwner = member?.roleKeys.includes("tenant_owner") ?? false;
  // An administrator cannot change an owner's roles at all; the database refuses, so the screen does not offer it.
  const rolesLocked = !canManage || (holdsOwner && !readerIsOwner);
  const suspended = member?.status === "suspended";
  const becomesPrivileged = roleKeys.some(isPrivilegedRole);
  const name = member === null ? "" : memberName(member);
  const campusOptions = campuses
    .filter((c) => c.isActive || campusIds.includes(c.id))
    .map((c) => ({ value: c.id, label: c.isActive ? c.name : `${c.name} (archived)` }));

  const save = async () => {
    if (member === null) return;
    if (roleKeys.length === 0) {
      setError("Choose at least one role.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const rolesChanged = !sameSet(roleKeys, member.roleKeys);
      const wantedCampuses = becomesPrivileged ? [] : campusIds;
      const campusesChanged = multiCampus && !sameSet(wantedCampuses, member.campusIds);
      if (becomesPrivileged) {
        // Campuses first: once someone is an administrator the database refuses to limit them, and a list left
        // behind would only mislead if they were moved back later.
        if (campusesChanged)
          await setCampuses.mutateAsync({ membershipId: member.membershipId, campusIds: [] });
        if (rolesChanged)
          await setRoles.mutateAsync({ membershipId: member.membershipId, roleKeys });
      } else {
        if (rolesChanged)
          await setRoles.mutateAsync({ membershipId: member.membershipId, roleKeys });
        if (campusesChanged)
          await setCampuses.mutateAsync({
            membershipId: member.membershipId,
            campusIds: wantedCampuses,
          });
      }
      notifications.show({
        color: semanticColor("success"),
        title: "Access saved",
        message: `${name}'s access changes the next time they load a page.`,
      });
      onClose();
    } catch (e) {
      setError(describeTeamError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={member !== null}
      onClose={onClose}
      title={`Access for ${name}`}
      radius="lg"
      size="lg"
    >
      <Stack gap="md">
        {member !== null &&
        member.email !== null &&
        member.email !== name &&
        !isApiKeyLogin(member) ? (
          <Text size="sm" c="dimmed">
            Signs in as {member.email}
            {member.status === "invited" ? " (invitation not yet accepted)" : ""}.
          </Text>
        ) : null}

        <RoleChoices
          value={roleKeys}
          onChange={setRoleKeys}
          customRoles={customRoles}
          offerOwner={readerIsOwner || holdsOwner}
          disabled={rolesLocked}
          description={
            holdsOwner && !readerIsOwner
              ? "Only an owner can change an owner's roles."
              : "Someone can hold more than one role; they can do what any of them allows."
          }
        />

        {multiCampus ? (
          <Stack gap={6}>
            <Title order={3} size="h5">
              Campuses
            </Title>
            {becomesPrivileged ? (
              <Text size="sm" c="dimmed">
                Owners and administrators always see every campus, so there is nothing to choose.
              </Text>
            ) : (
              <MultiSelect
                label="Works with"
                description="They see and manage these campuses' groups, events, check-in and plans, plus anything with no campus. Leave empty for every campus. The people directory stays church-wide."
                placeholder={campusIds.length === 0 ? "Every campus" : undefined}
                data={campusOptions}
                value={campusIds}
                onChange={setCampusIds}
                disabled={!canManage || suspended}
                {...(suspended ? { error: "Reactivate their login to change campuses." } : {})}
                clearable
                searchable
              />
            )}
          </Stack>
        ) : null}

        {member === null ? null : (
          <LoginStatusSection
            member={member}
            name={name}
            canManage={canManage}
            isSelf={readerUserId !== null && member.userId === readerUserId}
            ownerLocked={holdsOwner && !readerIsOwner}
            isLastOwner={isLastOwner}
            onDone={onClose}
          />
        )}

        {error === null ? null : (
          <Alert color={semanticColor("danger")} variant="light" role="alert">
            {error}
          </Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>
            {canManage ? "Cancel" : "Close"}
          </Button>
          {canManage ? (
            <Button
              loading={saving}
              onClick={() => {
                void save();
              }}
            >
              Save access
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Modal>
  );
}

interface LoginStatusSectionProps {
  readonly member: TeamMember;
  readonly name: string;
  readonly canManage: boolean;
  readonly isSelf: boolean;
  readonly ownerLocked: boolean;
  readonly isLastOwner: boolean;
  readonly onDone: () => void;
}

/** What each change does, said in the confirmation and the notification. */
function statusWords(change: LoginStatusChange, invited: boolean, name: string) {
  if (change === "suspended")
    return {
      title: `Suspend ${name}?`,
      message: `${name} will not be able to sign in to your church until you reactivate them. Their roles, campuses, person record and history stay as they are.`,
      confirm: "Suspend login",
      done: `${name} is suspended. Reactivate them from Team at any time.`,
    };
  if (change === "removed" && invited)
    return {
      title: `Cancel ${name}'s invitation?`,
      message: `The invitation sent to ${name} stops working. Their person record stays in People, and you can invite them again later.`,
      confirm: "Cancel invitation",
      done: `${name}'s invitation is cancelled.`,
    };
  if (change === "removed")
    return {
      title: `Remove ${name} from the church?`,
      message: `${name} will no longer be able to sign in to your church's dashboard or member portal, and their roles here end. Their person record and history stay in People, and you can invite them again later.`,
      confirm: "Remove from church",
      done: `${name} is removed from the church. Their person record stays in People.`,
    };
  return { title: "", message: "", confirm: "", done: `${name} can sign in again.` };
}

/**
 * Suspend, reactivate or remove a login. A suspended login keeps its roles and campuses but cannot sign in; a removed
 * one leaves the church (its person record and history stay) and can be invited again. Not your own login, not an
 * owner unless you are one, never the church's only owner; the database refuses the same.
 */
function LoginStatusSection({
  member,
  name,
  canManage,
  isSelf,
  ownerLocked,
  isLastOwner,
  onDone,
}: LoginStatusSectionProps) {
  const setStatus = useSetMembershipStatus();
  const [asking, setAsking] = useState<LoginStatusChange | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invited = member.status === "invited";
  const suspended = member.status === "suspended";

  const reason = !canManage
    ? null
    : isApiKeyLogin(member)
      ? "An API key's login ends when the key is revoked."
      : isSelf
        ? "You cannot suspend or remove your own login. Another owner or administrator can."
        : ownerLocked
          ? "Only an owner can suspend or remove an owner."
          : isLastOwner
            ? "The church's only owner cannot be suspended or removed. Make someone else an owner first."
            : null;

  const run = (change: LoginStatusChange) => {
    setError(null);
    setStatus.mutate(
      { membershipId: member.membershipId, status: change },
      {
        onSuccess: () => {
          setAsking(null);
          notifications.show({
            color: semanticColor("success"),
            title:
              change === "active"
                ? "Reactivated"
                : change === "suspended"
                  ? "Suspended"
                  : "Removed",
            message: statusWords(change, invited, name).done,
          });
          onDone();
        },
        onError: (e) => {
          setAsking(null);
          setError(describeStatusError(e));
        },
      },
    );
  };

  const words = asking === null ? null : statusWords(asking, invited, name);
  return (
    <Stack gap={6}>
      <Divider />
      <Title order={3} size="h5">
        Sign-in
      </Title>
      <Text size="sm" c="dimmed">
        {suspended
          ? "Suspended: they cannot sign in to your church. Their roles and campuses are kept."
          : invited
            ? "Invited: they have not accepted yet. The roles above are theirs once they do."
            : "Active: they can sign in with the roles above."}
      </Text>
      {!canManage ? null : reason !== null ? (
        <Text size="sm" c="dimmed">
          {reason}
        </Text>
      ) : (
        <Group gap="sm">
          {suspended ? (
            <Button
              variant="default"
              loading={setStatus.isPending && setStatus.variables.status === "active"}
              onClick={() => {
                run("active");
              }}
            >
              Reactivate login
            </Button>
          ) : invited ? null : (
            <Button
              variant="default"
              onClick={() => {
                setAsking("suspended");
              }}
            >
              Suspend login
            </Button>
          )}
          <Button
            variant="outline"
            color={semanticColor("danger")}
            onClick={() => {
              setAsking("removed");
            }}
          >
            {invited ? "Cancel invitation" : "Remove from church"}
          </Button>
        </Group>
      )}
      {error === null ? null : (
        <Alert color={semanticColor("danger")} variant="light" role="alert">
          {error}
        </Alert>
      )}
      {words === null || asking === null ? null : (
        <ConfirmActionModal
          opened
          onClose={() => {
            setAsking(null);
          }}
          onConfirm={() => {
            run(asking);
          }}
          title={words.title}
          message={words.message}
          confirmLabel={words.confirm}
          danger
          loading={setStatus.isPending}
        />
      )}
    </Stack>
  );
}
